import type { FC } from 'react'
import { useState } from 'react'
import styled from '@emotion/styled'

import ButtonText from '@segment/matcha/components/ButtonText'

import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import * as CopyText from '@segment/matcha/recipes/CopyText'

import HeroBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'

import EmailCaptureForm from '@segment/templates/Forms/EmailCaptureForm'

import { scrollToAnchor } from '@segment/utils/useScrollToAnchor'

import heroPng2x from './assets/Hero@2x.png'
import heroWebp from './assets/Hero.webp'
import heroWebp2x from './assets/Hero@2x.webp'

const copy = {
  heading: `The State of Personalization Report 2023`,
  description: `By Twilio Segment`,
}

const form = {
  formId: `state-of-personalization-report-2023`,
  additionalTrackEvent: `Content Downloaded`,
  buttonText: `Get the full report`,
  successButtonText: `Download the report`,
  buttonUrl: `https://segment.com/pdfs/State-of-Personalization-Report-Twilio-Segment-2023.pdf`,
}

const cta = {
  text: `Talk to a Segment expert`,
}

const Hero: FC = () => {
  const [isFormSubmitted, setIsFormSubmitted] = useState(false)

  return (
    <Wrapper>
      <StyledHeroBase variant="2Col">
        <HeroRecipe.Copy>
          <CopyText.Heading variant="h1" tag="h1">
            {copy.heading}
          </CopyText.Heading>
          <CopyText.Description variant="pExtraLarge" tag="p">
            {copy.description}
          </CopyText.Description>
          <HeroForm variant={isFormSubmitted ? `wide` : `narrow`}>
            <EmailCaptureForm
              customFormIdentifier={form.formId}
              additionalTrackEvent={form.additionalTrackEvent}
              resourceType="report"
              buttonText={form.buttonText}
              successButtonText={form.successButtonText}
              buttonUrl={form.buttonUrl}
              onSubmit={() => setIsFormSubmitted(true)}
              enableJobTitleField={true}
            />
          </HeroForm>
          <HeroCta onClick={() => scrollToAnchor(`get-most-out-of-customer-data`)} iconEnd={ChevronRightIcon}>
            {cta.text}
          </HeroCta>
        </HeroRecipe.Copy>

        <HeroRecipe.Contents>
          <picture>
            <source type="image/webp" srcSet={`${heroWebp}, ${heroWebp2x} 2x`} />
            <HeroRecipe.Image width="640" height="480" src={heroPng2x} alt={`Illustration: ${copy.heading}`} />
          </picture>
        </HeroRecipe.Contents>
      </StyledHeroBase>
    </Wrapper>
  )
}

const Wrapper = styled.header`
  background-color: ${({ theme }) => theme.palette.twilio.green[10]};
`

const StyledHeroBase = styled(HeroBase)`
  ${HeroRecipe.Copy} {
    text-align: left;
  }
`

interface HeroFormProps {
  variant: `wide` | `narrow`
}

const HeroForm = styled(HeroRecipe.Form)<HeroFormProps>`
  max-width: ${({ variant }) => (variant === `narrow` ? `417px` : `unset`)};
`

const HeroCta = styled(ButtonText)`
  margin-top: 16px;
`

export default Hero
